import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DateUtility } from '../../../../utilities/date.utility';
import { AnalysisManager } from '../../../../managers/analysis.manager';
import { Fact } from '../../../../models/ui/fact.model';
import { Router } from '@angular/router';

@Component({
    selector: 'lex-facts-timeline',
    templateUrl: './facts-timeline.component.html',
    styleUrls: ['./facts-timeline.component.scss'],
    standalone: false
})
export class FactsTimelineComponent implements OnInit {
	public rows = new Array<Array<FactCell>>();
	public totalDays: number = 0;
	public dayWidth: number = 0;
	public minDays: number = 30;
	public defaultWidth: number = 0;
	public maxWidth: number = 1600;
	public scale: number = 1;
	public collapseGaps: boolean = false;
	public earliestDate: Date;
	public latestDate: Date;
	public yearsAndTicks: any[];
	public facts: Fact[];

	private years: number[];

	@ViewChild('timelineContainer', { static: false }) mainContainer: ElementRef;

	constructor(private dateUtility: DateUtility, private analysisManager: AnalysisManager, private router: Router) {
		this.analysisManager.filteredFacts$.subscribe((factCollection) => {
			this.facts = factCollection.items;
			this.refreshTimeline();
		});
	}

	public ngOnInit(): void {
		setTimeout(() => {
			this.refreshTimeline();
		});
	}

	public trackRow(index: number) {
		return index;
	}

	public trackCell(index: number, cell: FactCell) {
		return cell?.fact?.id;
	}

	public trackYear(index: number) {
		return index;
	}

	public refreshTimeline(): void {
		this.defaultWidth = this.mainContainer?.nativeElement?.offsetWidth;
		this.rows = new Array<Array<FactCell>>();
		const sortedFacts = this.facts
			.filter((fact) => fact.dateStart && fact.dateEnd)
			.map((fact) => {
				if (fact.dateStart === fact.dateEnd) {
					fact.dateEnd = this.dateUtility.add(new Date(fact.dateStart), 1, 'days').toDateString();
				}
				return fact;
			})
			.sort((a, b) => Date.parse(a.dateStart) - Date.parse(b.dateStart));

		const sortedFactCells = sortedFacts.map(f => {
			const cell = new FactCell();
			cell.fact = f;
			return cell;
		});
		
		let earliestDate = new Date(8640000000000000);
		let latestDate = new Date(-8640000000000000);
		sortedFacts.forEach((fact) => {
			const dateStart = new Date(fact.dateStart);
			const dateEnd = new Date(fact.dateEnd ?? fact.dateStart);
			if (earliestDate > dateStart)
				earliestDate = dateStart;
			if (latestDate < dateEnd)
				latestDate = dateEnd;
		});

		this.totalDays = this.dateUtility.diff(earliestDate, latestDate)?.days;
		this.earliestDate = earliestDate;
		this.latestDate = latestDate;
		const yearCount = latestDate.getFullYear() - earliestDate.getFullYear();
		this.years = Array(yearCount + 1).fill(earliestDate.getFullYear()).map((y, i) => y + i);
		
		this.fillYearAndTicks();
		
		sortedFactCells.forEach((factCell) => {
			const startIndex = this.dateUtility.diff(earliestDate, new Date(factCell.fact.dateStart))?.days;
			if (startIndex >= 0) {
				factCell.startDay = startIndex;
			}
			const endIndex = this.dateUtility.diff(earliestDate, new Date(factCell.fact.dateEnd ?? factCell.fact.dateStart))?.days;
			if (endIndex >= 0) {
				factCell.endDay = endIndex;
			}
		});
		
		let lastStart = 0;
		//Set min days
		sortedFactCells.forEach((f, i) => {
			if (f.totalDays >= this.minDays)
				return;

			const deltaDays = this.minDays - f.totalDays;
			if (!f.startDay) {
				f.startDay = lastStart;
			}

			if (!f.endDay) {
				f.endDay = f.startDay;
			}
			f.endDay += deltaDays;
			this.totalDays += deltaDays;
			for (let j = 0; j < sortedFactCells.length; j++) {
				if (j == i) continue;
				if (sortedFactCells[j].startDay > f.startDay)
					sortedFactCells[j].startDay += deltaDays;
				if (sortedFactCells[j].endDay >= f.startDay)
					sortedFactCells[j].endDay += deltaDays;
			}
			for (let j = 0; j < this.yearsAndTicks.length; j++) {
				if (this.yearsAndTicks[j].startDay > f.startDay)
					this.yearsAndTicks[j].startDay += deltaDays;
				if (this.yearsAndTicks[j].endDay >= f.startDay)
					this.yearsAndTicks[j].endDay += deltaDays;
			}
			lastStart = f.startDay;

		});
		
		if (this.collapseGaps) {
			let currentDay = 0;
			sortedFactCells.forEach((f, i) => {
				if (f.startDay > currentDay) {
					const gap = f.startDay - currentDay - 1;
					this.totalDays -= gap;

					for (let j = i; j < sortedFactCells.length; j++) {
						sortedFactCells[j].startDay -= gap;
						sortedFactCells[j].endDay -= gap;
					}

					currentDay = f.endDay;
				}
				if (f.startDay <= currentDay && f.endDay > currentDay)
					currentDay = f.endDay;
			});
		}
		let rowAndOverflow = [[], sortedFactCells];
		while (rowAndOverflow[1].length > 0) {
			rowAndOverflow = this.fillRow(rowAndOverflow[1]);
			this.rows.push(rowAndOverflow[0]);
		}
		
		this.maxWidth = this.defaultWidth * this.scale;
		this.dayWidth = this.maxWidth / this.totalDays;
	}

	public convertDateTimeToDateString(dateTime: Date) : string {
		return dateTime.toLocaleString('en-Us', { day: "2-digit", month: "2-digit", year: "numeric" });
	}

	public editFact(fact: Fact) {
		this.router.navigate(['/', 'analytics', 'facts-and-issues', 'fact', fact.id]);
	}

	public createToolTip(cell: FactCell): string {
		const start = this.dateUtility.format(cell.fact.dateStart, 'M/d/y');
		const end = this.dateUtility.format(cell.fact.dateEnd, 'M/d/y');
		return `${ cell.fact.title } (${ start } - ${ end })`;
	}

	private fillRow(factCells: Array<FactCell>): Array<Array<FactCell>> {
		const row = new Array<FactCell>();
		const overflow = new Array<FactCell>();
		let currentDay: number = -1;
		factCells.forEach(f => {
			if (f.startDay > currentDay) {
				const emptyCellLength = f.startDay - currentDay;
				const cell = new FactCell();
				cell.startDay = currentDay + 1;
				if (emptyCellLength > 1) {
					cell.endDay = currentDay + emptyCellLength;
				} else {
					cell.endDay = cell.startDay;
				}
				row.push(cell);
				row.push(f);
				currentDay = f.endDay;
			} else {
				overflow.push(f);
			}
		});

		return [row, overflow];
	}

	private fillYearAndTicks() {
		const earliestDateYear = this.earliestDate.getFullYear();
		if (this.years.length == 1) {
			const yearCell = new YearCell();
			yearCell.year = earliestDateYear;
			yearCell.startDay = 0;
			yearCell.endDay = this.totalDays;
			this.yearsAndTicks = [yearCell];

			return;
		}

		this.yearsAndTicks = [];
		let currentDay = 0;

		for (let i = 0; i < this.years.length; i++) {
			if (i == 0) {
				const year = this.earliestDate.getFullYear();
				const lastDayOfTheYear = new Date(year, 11, 31);
				const dayCount = this.dateUtility.diff(this.earliestDate, lastDayOfTheYear).days;
				const yearCell = new YearCell();
				yearCell.year = year;
				yearCell.startDay = currentDay;
				yearCell.endDay = dayCount + currentDay;

				this.yearsAndTicks.push(yearCell);
				currentDay = yearCell.endDay;
			} else if (i == this.years.length - 1) {
				const year = this.latestDate.getFullYear();
				const firstDayOfTheYear = new Date(year, 0);
				const dayCount = this.dateUtility.diff(firstDayOfTheYear, this.latestDate).days;

				const yearCell = new YearCell();
				yearCell.year = year;
				yearCell.startDay = currentDay;
				yearCell.endDay = dayCount + currentDay;

				this.yearsAndTicks.push(yearCell);
				currentDay = yearCell.endDay;
			} else {
				const year = this.years[i];
				const days = this.dateUtility.getDaysInYear(year);

				const yearCell = new YearCell();
				yearCell.year = year;
				yearCell.startDay = currentDay;
				yearCell.endDay = days + currentDay;

				this.yearsAndTicks.push(yearCell);
				currentDay = yearCell.endDay;
			}
			currentDay++;
		}
	}
}

class FactCell {
	fact: Fact;
	startDay: number;
	endDay: number;

	get totalDays() {
		return this.endDay - this.startDay || 1;
	}
}

class YearCell {
	year: number;
	startDay: number;
	endDay: number;

	get totalDays() {
		return this.endDay - this.startDay || 1;
	}
}
